/**
 * headAim — pure yaw math for the head look-at (ENG-609).
 *
 * Extracted from headFollowRuntime so the "clamp head yaw relative to the body"
 * decision is unit-testable without THREE / the render loop. No imports.
 *
 * The bug it fixes: the head look-at used to clamp yaw against a FIXED world
 * forward (+Z). That's fine while the avatar stands still facing +Z, but
 * locomotion (movementComponent) yaws the whole avatar group to face the walk
 * direction. With a world-anchored clamp the head would aim at the camera
 * regardless of where the body points, so head-relative-to-torso could reach
 * body-yaw + maxYaw — well past a neck's range ("exorcist twist"). Clamping
 * relative to the body's current forward keeps the head within ±maxYaw of the
 * torso no matter which way the body faces.
 */

/** Wrap an angle (radians) into (-π, π]. */
export function normalizeAngle(a) {
  const twoPi = Math.PI * 2
  a = a % twoPi
  if (a > Math.PI) a -= twoPi
  if (a <= -Math.PI) a += twoPi
  return a
}

/**
 * Clamp the desired world yaw to within ±maxYaw of the body's forward yaw, and
 * return the result RELATIVE to the body (so the caller can smooth it and then
 * re-add bodyYaw to get the world-space look direction).
 *
 * @param {number} rawYaw   desired look yaw in world space (rad; 0 = +Z)
 * @param {number} bodyYaw  body forward yaw in world space (rad; group.rotation.y)
 * @param {number} maxYaw   max head deviation from the body (rad, ≥ 0)
 * @returns {number} clamped yaw relative to the body forward, in [-maxYaw, maxYaw]
 */
export function clampYawToBody(rawYaw, bodyYaw, maxYaw) {
  const rel = normalizeAngle(rawYaw - bodyYaw)
  return Math.max(-maxYaw, Math.min(maxYaw, rel))
}
